import { Reel } from '../components/reel';
import { addTouchControl } from '../../helpers/touchSpinControll';
import { gameModuleStore } from '../store/gameModuleStore';
import { GAME_SPEED_TYPES } from '../../constants/gameConstants';
import { gameResourcesService } from './gameResourcesService';
import { handleSymbolAnimationPositionInMatrix } from '../../helpers/createWinLinesMatrix';
import { iconStates, specialIconKeys } from '../../constants/symbolsNames';
import {
  createCustomEases,
  handleReelIconStateInWinCase,
  handleWinLineBonePosition,
  setupPixiPlugins,
} from '../../helpers/gameModule';
import { gameConfigurationStore } from '../store/gameConfigurationStore';
import { skippablePromise } from '../../helpers/skippablePromise';
import { gameGlobalStates } from '../../constants/reelConstants';

const WIN_LINE_ANIMATION = 'animation';
// RU: столько держится на табло выигрыш без линии (scatter) в цикле показа по линиям.
const SCATTER_WIN_SHOW_MS = 1000;

class GameModuleService {
  reels = [];
  reelsContainer = null;
  winContainer = null;
  touchSpinScreen = null;
  stopNextReel = this.reelsStopGenerator();
  skippableAnimationPromise = [];
  winLineTimeOutId = null;
  // RU: номер текущего цикла показа по линиям. Цикл асинхронный и между линиями ждёт конца
  //     анимации; новый показ (или новый спин) поднимает номер, и прежний цикл выходит на
  //     ближайшей проверке — два цикла одновременно по одной доске не идут.
  byLineRun = 0;

  constructor() {
    this.promiseControl = skippablePromise();
  }

  startSpin = () => {
    gameModuleStore.setGameGlobalState(gameGlobalStates.waiting);
    gameModuleStore.setResultMatrix([]);
    gameModuleStore.setCanTouchControl(false);
    gameModuleStore.setScatterCount('reset');
    clearTimeout(this.winLineTimeOutId);
    this.endAllWinAnimations();
    this.reels.forEach((reel) => {
      reel.canStop = false;
      reel.startSpin();
      reel.isForceStop = false;
    });
  };

  // RU: раскрытие wild без выигрышных линий — тоже законный исход: раунд закрывается как
  //     проигрышный. Прежде он уходил в показ выигрыша с пустым списком и падал на первой
  //     линии (`winLinesData[0]` — undefined).
  startWildAnimation = (reelsIndex, wins) => {
    this.skippableAnimationPromise = this.reels
      .filter((reel, i) => reelsIndex[i] !== 0)
      .map((reel) => reel.startWildAnimation());
    this.promiseControl.runPromise(this.skippableAnimationPromise, () => {
      if (wins.length) {
        this.startWinAnimation(wins);
      } else {
        this.skippableAnimationPromise = [];
        gameModuleStore.finishRoundWithoutWin();
      }
    });
  };

  // RU: forceStop посреди показа — досадить раскрытие wild и выигрышные анимации в конечный
  //     кадр (линии ускорить) и оборвать ожидание, чтобы раунд отчитался той же стадией.
  skipAnimations = () => {
    gameModuleStore.wildIcons.forEach((icon) => {
      icon.icon.goToAnimationEnd(icon.currentState);
    });
    gameModuleStore.winAnimations.forEach((winAnimation) => {
      if (winAnimation.icon) {
        winAnimation.icon.goToAnimationEnd(winAnimation.currentState);
      } else {
        winAnimation.accelerateCurrentAnimation(3);
      }
    });
    gameModuleStore.setWildIcons();
    gameModuleStore.setWinAnimations();
    this.promiseControl.skipPromise();
  };

  stopAnimation = (matrix) => {
    gameModuleStore.setResultMatrix(matrix);
    gameModuleStore.setResponseReady(true);
    if (gameModuleStore.spinSpeedType !== GAME_SPEED_TYPES.turbo) this.stopNextReel.next();
    this.reels.forEach((reel) => {
      reel.initFromResponseData(matrix);
      if (gameModuleStore.spinSpeedType === GAME_SPEED_TYPES.turbo) reel.stopTurboReelAnimation();
    });
    gameModuleStore.setSpinDirection(1);
  };

  *reelsStopGenerator() {
    let i = 0;
    while (true) {
      yield (this.reels[i].canStop = true);
      i = (i + 1) % this.reels.length;
    }
  }

  createReels = (reelsContainer, winContainer, touchSpinScreen) => {
    this.setReelsContainerAndWinContainer(reelsContainer, winContainer, touchSpinScreen);
    this.initReels();
  };

  setReelsContainerAndWinContainer(reelsContainer, winContainer, touchSpinScreen) {
    this.reelsContainer = reelsContainer;
    this.winContainer = winContainer;
    this.touchSpinScreen = touchSpinScreen;
  }

  initReels() {
    for (let reelIndex = 0; reelIndex < gameConfigurationStore.reelsParams.length; reelIndex++) {
      const reel = new Reel({
        index: reelIndex,
        reelsContainer: this.reelsContainer,
        slotWinLayer: this.winContainer,
        isLastReel: reelIndex === gameConfigurationStore.reelsParams.length - 1,
        stopNextReel: this.stopNextReel,
        reelParams: gameConfigurationStore.reelsParams,
      });
      this.reels.push(reel);
    }
    setupPixiPlugins();
    createCustomEases();
    addTouchControl(this.touchSpinScreen, this.winContainer, this.reels, this.reelsContainer);
  }

  startWinAnimation = (wins) => {
    const scatterWinType = wins.find((el) => !el.line);
    const symbolWinType = wins.some((el) => el.line);
    const { winLines, allLinesMatrix } = handleSymbolAnimationPositionInMatrix(wins);
    gameModuleStore.soundService.play('WinningLineSound');
    this.skippableAnimationPromise = this.allWinAnimation(winLines, allLinesMatrix);
    this.promiseControl.runPromise(this.skippableAnimationPromise, () => {
      this.byLineWinAnimation(winLines);
      // RU: свайп поднимается ДО отчёта ядру: на этом отчёте ядро может сразу запустить
      //     следующий спин (автоигра), и его `startSpin` должен опустить флаг последним.
      gameModuleStore.setCanTouchControl(true);
      gameModuleStore.animationEnd('allLineAnimationEnd', 0, scatterWinType && !symbolWinType);
      this.skippableAnimationPromise = [];
    });
  };

  allWinAnimation = (winLines, allLinesMatrix) => {
    const animationPromises = [];
    gameModuleStore.animationEnd('allLineAnimationStart');
    this.reels.forEach((reel) => {
      animationPromises.push(
        ...reel.startWinAnimation(handleReelIconStateInWinCase(allLinesMatrix, reel))
      );
    });
    winLines.forEach(({ line, lines }) => {
      if (line) {
        this.reels.forEach((reel) => {
          const index = reel.index;
          gameResourcesService.allWinLinesBone[line - 1][index].y = handleWinLineBonePosition(
            lines,
            reel
          );
        });
        const spineLine = gameResourcesService.allLinesSpine[line - 1];
        spineLine.visible = true;
        spineLine.play(WIN_LINE_ANIMATION);
        gameModuleStore.setWinAnimations(spineLine);
        animationPromises.push(
          new Promise((animResolve) => {
            spineLine.onceAnimationEvent(
              'complete',
              () => {
                spineLine.visible = false;
                animResolve();
              },
              WIN_LINE_ANIMATION
            );
          })
        );
      }
    });

    return animationPromises;
  };

  async byLineWinAnimation(winLinesData) {
    this.byLineRun += 1;
    const run = this.byLineRun;
    let start = 0;
    while (run === this.byLineRun && gameModuleStore.gameGlobalState === gameGlobalStates.show) {
      const winLine = winLinesData[start];
      const { line, lines } = winLine;
      const lineIndex = line - 1;
      // RU: в списке — только анимации ТЕКУЩЕЙ линии: прежде каждый проход дописывал в него
      //     все иконки окна заново, и за время простоя он рос без предела.
      gameModuleStore.setWinAnimations();
      for (let i = 0; i < this.reels.length; i++) {
        const column = this.reels[i];
        const { visibleRows } = gameConfigurationStore.reelsParams[i];
        for (let j = 0; j < visibleRows; j++) {
          const icon = column.icons[j + visibleRows];
          gameModuleStore.setWinAnimations(icon);
          if (lines[j][i] === 1) {
            icon.changeState(iconStates.win);
          } else {
            icon.changeState(
              icon.iconNumber === specialIconKeys.wild ? iconStates.idle : iconStates.noWin
            );
          }
          if (lines[j][i] && line) {
            gameResourcesService.allWinLinesBone[lineIndex][i].y = handleWinLineBonePosition(
              lines,
              this.reels[i]
            );
          }
        }
      }
      // RU: табло ядра показывает линию по ОБЪЕКТУ winLine (line / count / symbol / win) —
      //     с одним номером линии оно писало «Scatter» и NaN.
      gameModuleStore.animationEnd('byLineAnimation', winLine);
      if (line) {
        const spineLine = gameResourcesService.allLinesSpine[lineIndex];
        spineLine.play(WIN_LINE_ANIMATION);
        gameModuleStore.setWinAnimations(spineLine);
        spineLine.visible = true;
        await new Promise((animResolve) => {
          spineLine.onceAnimationEvent(
            'complete',
            () => {
              spineLine.visible = false;
              animResolve();
            },
            WIN_LINE_ANIMATION
          );
        });
      } else {
        await new Promise((resolve) => {
          this.winLineTimeOutId = setTimeout(resolve, SCATTER_WIN_SHOW_MS);
        });
      }
      // RU: в реплее (и интро) конец первого прохода — терминал раунда. Считается по ИНДЕКСУ
      //     записи, а не по её типу: прежде отчёт стоял внутри ветки линии, и если последней
      //     записью шёл scatter, терминал не уходил вовсе — реплей не заканчивался.
      if (
        run === this.byLineRun &&
        (gameConfigurationStore.isReplay || gameModuleStore.isIntro) &&
        start === winLinesData.length - 1
      ) {
        gameModuleStore.animationEnd('byLineAnimationEnd');
      }
      start = (start + 1) % winLinesData.length;
    }
  }

  endAllWinAnimations = () => {
    this.byLineRun += 1;
    gameModuleStore.winAnimations.forEach((animation) => {
      if (animation.icon) {
        animation.icon.goToAnimationEnd(animation.currentState);
      } else {
        animation.goToAnimationEnd(animation.state.tracks[0]?.animation.name);
      }
    });
    gameModuleStore.setWinAnimations();
  };
}

export const gameModuleService = new GameModuleService();
